import type { Result } from '@repo/utils'

import { type Chain, chains, type Chains } from '../configs/chains/chain.config'
import { ChainId, type MaybeChainId } from '../web3.types'

/**
 * Compares two chain IDs to determine if they represent the same chain.
 * @param chainId1 - The first chain ID to compare
 * @param chainId2 - The second chain ID to compare
 * @returns True if both chain IDs represent the same chain, false otherwise
 */
export function isSameChain(chainId1: MaybeChainId, chainId2: MaybeChainId): boolean {
  const id1 = typeof chainId1 === 'number' ? chainId1 : Number(chainId1)
  const id2 = typeof chainId2 === 'number' ? chainId2 : Number(chainId2)

  if (isNaN(id1) || isNaN(id2)) return false

  return id1 === id2
}

/**
 * Validates and parses the chainId from a raw string or number.
 * @param chainId - The chain ID to validate (can be number or string)
 * @returns A Result object containing the validated chainId if valid, or an error message
 */
export function validateChainId(chainId: MaybeChainId): Result<number> {
  const parsed = typeof chainId === 'number' ? chainId : Number(chainId)

  if (isNaN(parsed) || !Number.isInteger(parsed) || parsed < 0) {
    return { data: null, error: 'Invalid chainId' }
  }

  if (!(parsed in ChainId)) {
    return { data: null, error: 'ChainId not supported' }
  }

  return { data: parsed, error: null }
}

/**
 * Type guard for narrowing an untrusted number (wire data, user input) to a known {@link ChainId}.
 * Prefer this over inlining `x in ChainId`: the `in` check works for numeric inputs (the enum's
 * reverse mapping), but it doesn't narrow the type and reads as though it might match enum *names*.
 */
export function isChainId(chainId: ChainId | number): chainId is ChainId {
  return chainId in ChainId
}

/**
 * Ordinary method overloading and conditional return types DO NOT WORK
 * in the `getChain` and `getChainConfig` methods -- something about
 * `chains` being `as const`, combined with the fact that `ChainId`
 * evaluates to a plain number, means that TypeScript can't decide
 * whether the input argument can be narrowed or not. So without this,
 * literals work fine, but any variable of type `number` that's passed
 * to `getChain` or `getChainConfig` would yield `unknown`.
 *
 * This helper ensures that passing generic numbers will result in a
 * union over the possible chain types.
 */
type ChainsReturnTypeHelper = {
  [chainId in keyof Chains]: Chains[chainId]
} & {
  [chainId: number]: Chain
}

/**
 * Retrieves the full chain object for a given chain ID.
 * If chain param is not in the `ChainId` enum, `mainnet` is returned.
 *
 * @param chainId - The chain ID to get configuration for
 * @returns The chain configuration object
 */
export function getChain<K extends ChainId>(chainId: K): ChainsReturnTypeHelper[K]
export function getChain(chainId: number): ChainsReturnTypeHelper[number]
export function getChain(chainId: number) {
  return chains[isChainId(chainId) ? chainId : ChainId.Mainnet]
}

/**
 * Retrieves the custom chain config for a given chain ID.
 * If chain param is not in the `ChainId` enum, the `mainnet` config is returned.
 *
 * @param chain - The chain ID to get configuration for
 * @returns The chain configuration object
 */
export function getChainConfig<K extends ChainId>(chainId: K): ChainsReturnTypeHelper[K]['custom']
export function getChainConfig(chainId: number): ChainsReturnTypeHelper[number]['custom']
export function getChainConfig(chainId: number) {
  return chains[isChainId(chainId) ? chainId : ChainId.Mainnet].custom
}

/**
 * Retrieves multiple chain configurations for given chain IDs.
 * @param chainIds - Array of chain IDs to get configurations for
 * @returns Tuple of chain configurations, with at least one chain
 */
export function getChains(chainIds: readonly ChainId[]): [Chain, ...Chain[]] {
  const [first, ...rest] = chainIds
  const result: [Chain, ...Chain[]] = [getChain(first ?? ChainId.Mainnet)]

  for (const chainId of rest) {
    const chain = getChain(chainId)
    if (chain) {
      result.push(chain)
    }
  }
  return result
}

/**
 * Gets the name of the chain.
 * @param chainId - The chain ID to get the name for
 * @returns The chain name
 */
export function getChainName(chainId: ChainId) {
  return getChainConfig(chainId).name
}

/**
 * Gets the short name of the chain.
 * @param chainId - The chain ID to get the short name for
 * @returns The chain short name
 */
export function getChainShortName(chainId: ChainId) {
  return getChainConfig(chainId)?.shortName ?? ''
}

/**
 * Gets the Morpho chain slug for the chain.
 * @param chainId - The chain ID to get the slug for
 * @returns The Morpho chain slug
 */
export function getMorphoChainSlug(chainId: ChainId) {
  return getChainConfig(chainId).morphoChainSlug
}

const chainIdByMorphoSlug = Object.fromEntries(
  Object.entries(chains).map(([id, c]) => [c.custom.morphoChainSlug, Number(id) as ChainId])
)

/**
 * Resolves a Morpho chain slug (e.g. `'base'`, `'ethereum'`) back to its `ChainId`.
 * Returns `undefined` when the slug does not match any known chain.
 */
export function getChainIdFromMorphoSlug(slug: string): ChainId | undefined {
  return chainIdByMorphoSlug[slug]
}

/**
 * Gets the Morpho app URL for a vault.
 * @param address - The vault address
 * @param chainId - The chain ID
 * @returns The Morpho app vault URL
 */
export function getMorphoVaultUrl(address: string, chainId: ChainId) {
  return `https://app.morpho.org/${getMorphoChainSlug(chainId)}/vault/${address}`
}

/**
 * Whether the chain has full Morpho platform support (indexed by api.morpho.org).
 */
export function hasFullSupport(chainId: ChainId): boolean {
  const config = getChainConfig(chainId)
  return 'hasFullSupport' in config && config.hasFullSupport === true
}
